import { useSyncExternalStore } from 'react';

// ── Types ──────────────────────────────────────────────────────
export type FontSize = 'normal' | 'large' | 'xlarge' | 'huge' | 'giant';
export type ThemeMode = 'light' | 'dark' | 'high-contrast';
export type ClockStyle = 'digital' | 'analog';
/**
 * Klokgrootte staat los van `fontSize`: de klok is het ene ding dat een bewoner van over
 * de kamer moet kunnen lezen, en die mag groter dan de grootste lettergrootte van de app.
 */
export type ClockSize = 'small' | 'medium' | 'large' | 'xlarge' | 'huge';
export type ScreensaverMode = 'photos' | 'clock-only';
/** Achtergrond van de standby-weergave (screensaver/slaapstand zonder foto). */
export type StandbyBackground = 'dark' | 'light';
/**
 * T6.5 / D6 — reduce-motion preference.
 * - `auto`: respect the OS-level `prefers-reduced-motion` media query (default).
 * - `on`:   force-disable animations / transitions app-wide regardless of OS.
 * - `off`:  force-enable animations even when the OS would suppress them.
 *
 * The effective value is applied at App-root by setting
 * `<html data-reduce-motion="true|false">`; a CSS rule in `global.css`
 * then collapses animation / transition durations to 0.01ms.
 */
export type ReduceMotionPreference = 'auto' | 'on' | 'off';

export interface DisplaySettings {
  brightness: number;   // 0–100
  /**
   * T7.1 / E1 — intentionally **session-only** (not persisted to localStorage).
   *
   * Rationale: `sleepMode` is a transient runtime flag toggled by:
   *   - `SleepOverlay` (auto-engages after 1 minute of idle as fallback screensaver).
   *   - `DisplaySettingsPage` (manual "Sleep now" button → `setSleep(true)`).
   *   - `ScreensaverController` (force-wakes via `setSleep(false)` on activity).
   *
   * Persisting it across reloads would trap residents on a black screen if
   * the app reloaded while sleep was engaged (e.g. crash, OTA update, Electron
   * restart) — they'd power-cycle the device to recover. By design,
   * `loadFromStorage()` always returns `sleepMode: false` and `saveToStorage()`
   * never writes the field. The shape stays in the interface because three
   * consumer components subscribe to it via `useDisplaySettings()`; the field
   * has real readers, just no persistence.
   *
   * If you find yourself wanting to persist this, persist a separate
   * `sleepOnLaunch: boolean` preference instead — never the transient flag.
   */
  sleepMode: boolean;
  fontSize: FontSize;
  theme: ThemeMode;
  screensaverMode: ScreensaverMode;
  /**
   * Whether the device is ALLOWED to enter the screensaver at all. Set per
   * location-type in the portal (app_config.displayDefaults.screensaverEnabled)
   * and pushed to the device. When false, ScreensaverController never engages.
   */
  screensaverEnabled: boolean;
  clockStyle: ClockStyle;
  clockSize: ClockSize;
  /** Vette cijfers/wijzers voor de klok (beginscherm, screensaver, slaapstand). */
  clockBold: boolean;
  /** Jaartal in de datum onder de klok tonen. */
  clockShowYear: boolean;
  weatherLocation: string; // e.g. "Deinze,Belgium" — empty = auto from building
  /** T6.5 / D6 — user-controllable motion reduction. Persists in localStorage. */
  reduceMotion: ReduceMotionPreference;
  /** Donkere of witte standby-achtergrond, per bewoner te kiezen. */
  standbyBackground: StandbyBackground;
  /**
   * Eindhelderheid van de echte schermverlichting in de slaapstand, 1–100 (%). De gewone
   * stand is `brightness` hierboven (100 = de helderheid van het toestel zelf). Beide
   * komen normaal uit het portaal: site-standaard, per appartement anders.
   */
  sleepBrightness: number;
}

const STORAGE_KEY = 'caro_display_settings';

// Bump when changing DEFAULT_SETTINGS in a way that should override values a
// device persisted under an OLDER default. Devices on an older stamp are
// force-migrated ONCE (see loadFromStorage); explicit choices made AFTER the
// migration carry the new stamp and are then respected on subsequent loads.
//   v2: dark theme + analog clock became the defaults.
//   v3: large-type accessibility default (`xlarge`) + digital clock became the
//       defaults — devices flip to xlarge/digital once, then stick.
const SETTINGS_VERSION = 3;

const FONT_SIZES: FontSize[] = ['normal', 'large', 'xlarge', 'huge', 'giant'];
export const CLOCK_SIZES: ClockSize[] = ['small', 'medium', 'large', 'xlarge', 'huge'];

const DEFAULT_SETTINGS: DisplaySettings = {
  brightness: 100,
  sleepMode: false,
  fontSize: 'xlarge',
  theme: 'dark',
  screensaverMode: 'photos',
  screensaverEnabled: true,
  clockStyle: 'digital',
  clockSize: 'medium',
  clockBold: false,
  clockShowYear: false,
  weatherLocation: '',
  reduceMotion: 'auto',
  standbyBackground: 'dark',
  sleepBrightness: 1,
};

// Shape pushed from the portal (per location-type display defaults). All keys
// optional — only provided keys are applied.
export interface DisplayDefaults {
  theme?: ThemeMode;
  clockStyle?: ClockStyle;
  fontSize?: FontSize;
  clockSize?: ClockSize;
  clockBold?: boolean;
  clockShowYear?: boolean;
  screensaverEnabled?: boolean;
  standbyBackground?: StandbyBackground;
  /** Gewone stand, 5–100 (%); 100 = de helderheid van het toestel. */
  brightness?: number;
  /** Slaapstand, 1–100 (%). */
  sleepBrightness?: number;
}

// ── Module-level store ─────────────────────────────────────────
let settings: DisplaySettings = { ...DEFAULT_SETTINGS };
const listeners = new Set<() => void>();

function loadFromStorage(): DisplaySettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw);
    // Storage on an older stamp predates the current defaults. Force the new
    // defaults ONCE so existing devices flip; after this the saved blob carries
    // `v: SETTINGS_VERSION` and explicit choices stick.
    //   - preV2 also flips theme → dark.
    //   - preV3 (which includes all v2 devices) flips fontSize → xlarge and
    //     clockStyle → digital.
    const preV2 = parsed.v !== 2 && parsed.v !== SETTINGS_VERSION;
    const preV3 = parsed.v !== SETTINGS_VERSION;
    return {
      brightness: typeof parsed.brightness === 'number' ? Math.max(0, Math.min(100, parsed.brightness)) : 100,
      sleepMode: false, // Never persist sleep mode — always wake on reload
      fontSize: preV3
        ? 'xlarge'
        : (FONT_SIZES.includes(parsed.fontSize) ? parsed.fontSize : 'xlarge'),
      theme: preV2
        ? 'dark'
        : (['light', 'dark', 'high-contrast'].includes(parsed.theme) ? parsed.theme : 'dark'),
      screensaverMode: ['photos', 'clock-only'].includes(parsed.screensaverMode) ? parsed.screensaverMode : 'photos',
      screensaverEnabled: typeof parsed.screensaverEnabled === 'boolean' ? parsed.screensaverEnabled : true,
      clockStyle: preV3
        ? 'digital'
        : (['digital', 'analog'].includes(parsed.clockStyle) ? parsed.clockStyle : 'digital'),
      clockSize: CLOCK_SIZES.includes(parsed.clockSize) ? parsed.clockSize : 'medium',
      clockBold: parsed.clockBold === true,
      clockShowYear: parsed.clockShowYear === true,
      weatherLocation: typeof parsed.weatherLocation === 'string' ? parsed.weatherLocation : '',
      reduceMotion: ['auto', 'on', 'off'].includes(parsed.reduceMotion) ? parsed.reduceMotion : 'auto',
      standbyBackground: parsed.standbyBackground === 'light' ? 'light' : 'dark',
      sleepBrightness: typeof parsed.sleepBrightness === 'number'
        ? Math.max(1, Math.min(100, Math.round(parsed.sleepBrightness)))
        : 1,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function saveToStorage() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      v: SETTINGS_VERSION,
      brightness: settings.brightness,
      fontSize: settings.fontSize,
      theme: settings.theme,
      screensaverMode: settings.screensaverMode,
      screensaverEnabled: settings.screensaverEnabled,
      clockStyle: settings.clockStyle,
      clockSize: settings.clockSize,
      clockBold: settings.clockBold,
      clockShowYear: settings.clockShowYear,
      weatherLocation: settings.weatherLocation,
      reduceMotion: settings.reduceMotion,
      standbyBackground: settings.standbyBackground,
      sleepBrightness: settings.sleepBrightness,
    }));
  } catch {
    // localStorage not available
  }
}

/**
 * Resolve the effective reduce-motion boolean from the tri-state preference.
 * `auto` reads the OS media query each time it's called; `on`/`off` are
 * explicit overrides. Returns `false` in non-browser environments (SSR/Jest)
 * where `matchMedia` is undefined.
 */
function resolveReduceMotion(pref: ReduceMotionPreference): boolean {
  if (pref === 'on') return true;
  if (pref === 'off') return false;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function applyToDOM() {
  const root = document.documentElement;
  // Font size
  if (settings.fontSize === 'normal') {
    root.removeAttribute('data-font-size');
  } else {
    root.setAttribute('data-font-size', settings.fontSize);
  }
  // Theme
  if (settings.theme === 'light') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', settings.theme);
  }
  // T6.5 / D6: reduce-motion. We write the attribute unconditionally
  // (true | false) — even `auto` resolves to a concrete value at this
  // moment — so the CSS selector in global.css ("[data-reduce-motion='true']")
  // works without falling back to a `prefers-reduced-motion` media query.
  // The OS-level media query rule still exists for parity; the attribute
  // wins because it's more specific.
  root.setAttribute(
    'data-reduce-motion',
    resolveReduceMotion(settings.reduceMotion) ? 'true' : 'false',
  );
}

function notify() {
  listeners.forEach((fn) => fn());
}

function update(partial: Partial<DisplaySettings>) {
  settings = { ...settings, ...partial };
  applyToDOM();
  saveToStorage();
  notify();
}

/**
 * Apply portal-pushed display defaults (per location-type). Validates each key
 * and only applies the ones provided. Callable from outside React (the MQTT/WS
 * command handler and the building-config bootstrap both use it).
 */
export function applyDisplayDefaults(defaults: DisplayDefaults | null | undefined) {
  if (!defaults || typeof defaults !== 'object') return;
  const partial: Partial<DisplaySettings> = {};
  if (['light', 'dark', 'high-contrast'].includes(defaults.theme as string)) {
    partial.theme = defaults.theme as ThemeMode;
  }
  if (['digital', 'analog'].includes(defaults.clockStyle as string)) {
    partial.clockStyle = defaults.clockStyle as ClockStyle;
  }
  if (FONT_SIZES.includes(defaults.fontSize as FontSize)) {
    partial.fontSize = defaults.fontSize as FontSize;
  }
  if (CLOCK_SIZES.includes(defaults.clockSize as ClockSize)) {
    partial.clockSize = defaults.clockSize as ClockSize;
  }
  if (typeof defaults.clockBold === 'boolean') {
    partial.clockBold = defaults.clockBold;
  }
  if (typeof defaults.clockShowYear === 'boolean') {
    partial.clockShowYear = defaults.clockShowYear;
  }
  if (typeof defaults.screensaverEnabled === 'boolean') {
    partial.screensaverEnabled = defaults.screensaverEnabled;
  }
  if (defaults.standbyBackground === 'dark' || defaults.standbyBackground === 'light') {
    partial.standbyBackground = defaults.standbyBackground;
  }
  if (typeof defaults.brightness === 'number' && defaults.brightness >= 5 && defaults.brightness <= 100) {
    partial.brightness = Math.round(defaults.brightness);
  }
  if (typeof defaults.sleepBrightness === 'number' && defaults.sleepBrightness >= 1 && defaults.sleepBrightness <= 100) {
    partial.sleepBrightness = Math.round(defaults.sleepBrightness);
  }
  if (Object.keys(partial).length > 0) update(partial);
}

// Initialize on module load
settings = loadFromStorage();
applyToDOM();
// Persist immediately so the migration (and SETTINGS_VERSION stamp) sticks —
// after this, explicit user theme/clock changes are respected on reload.
saveToStorage();

// T6.5 / D6: when the user picks `auto`, re-evaluate the OS-level
// `prefers-reduced-motion` query whenever it changes. The listener fires
// in both directions (true ↔ false). We only `applyToDOM()` — no state
// mutation — so subscribers don't get unnecessary re-renders when the
// settings object itself is unchanged.
if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  try {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => {
      if (settings.reduceMotion === 'auto') applyToDOM();
    };
    // Older Safari uses `addListener` / `removeListener`. The modern
    // `addEventListener('change', …)` is preferred where available.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
    } else if (typeof (mql as MediaQueryList & { addListener?: (cb: () => void) => void }).addListener === 'function') {
      (mql as MediaQueryList & { addListener: (cb: () => void) => void }).addListener(onChange);
    }
  } catch {
    // matchMedia not supported — auto degrades to "always off", per resolveReduceMotion.
  }
}

// ── Public API ─────────────────────────────────────────────────
function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}

function getSnapshot(): DisplaySettings {
  return settings;
}

export function useDisplaySettings() {
  const current = useSyncExternalStore(subscribe, getSnapshot);

  return {
    settings: current,

    setBrightness(value: number) {
      update({ brightness: Math.max(0, Math.min(100, Math.round(value))) });
    },

    toggleSleep() {
      update({ sleepMode: !settings.sleepMode });
    },

    setSleep(on: boolean) {
      update({ sleepMode: on });
    },

    setFontSize(size: FontSize) {
      update({ fontSize: size });
    },

    setTheme(theme: ThemeMode) {
      update({ theme });
    },

    setScreensaverMode(mode: ScreensaverMode) {
      update({ screensaverMode: mode });
    },

    setStandbyBackground(background: StandbyBackground) {
      update({ standbyBackground: background });
    },

    setScreensaverEnabled(enabled: boolean) {
      update({ screensaverEnabled: enabled });
    },

    setSleepBrightness(value: number) {
      update({ sleepBrightness: Math.max(1, Math.min(100, Math.round(value))) });
    },

    setClockStyle(style: ClockStyle) {
      update({ clockStyle: style });
    },

    setClockSize(size: ClockSize) {
      update({ clockSize: size });
    },

    setClockBold(bold: boolean) {
      update({ clockBold: bold });
    },

    setClockShowYear(show: boolean) {
      update({ clockShowYear: show });
    },

    setWeatherLocation(location: string) {
      update({ weatherLocation: location.trim() });
    },

    /** T6.5 / D6 — three-state reduce-motion control. */
    setReduceMotion(pref: ReduceMotionPreference) {
      update({ reduceMotion: pref });
    },
  };
}
